import React, { Suspense, lazy, useEffect } from 'react';
import { useMotion } from '../../hooks/useMotion.js';
// Three.js is by far the largest dependency and only this page uses it, so the 3D
// scene is split into its own chunk instead of weighing down every page's load.
const Roadmap3D = lazy(() => import('./Roadmap3D.jsx').then((m) => ({ default: m.Roadmap3D })));
import './BusinessModel.css';

const roadmapSteps = [
  {
    step: '01',
    badge: 'Account Setup',
    title: 'Private Bank Account Opening',
    text: 'Client opens an account with the private bank of their choice. If the client already holds an existing account with an approved custodian bank, no new account is required.',
    image: '/media/corporate-professional-project-talking-accountant-workplace.webp',
    parties: ['Client', 'Custodian Bank'],
  },
  {
    step: '02',
    badge: 'Authorisation',
    title: 'Limited Power of Attorney (LPOA)',
    text: 'Client signs an LPOA (a) to appoint ASTRID FIRST as independent asset manager. The LPOA gives ASTRID the authority to invest and manage, but strictly forbids transferring assets out of the account.',
    image: '/media/businessmen-discussing-plans-with-small-wooden-hou-2026-09-21-18-34-17-utc.webp',
    parties: ['Client', 'ASTRID FIRST'],
  },
  {
    step: '03',
    badge: 'Custody Security',
    title: 'Segregated Asset Custody',
    text: 'ASTRID FIRST does not hold the client\'s assets. All cash, securities and portfolio investments remain custodied directly with the private bank under the client\'s own legal name.',
    image: '/media/landscape-view-of-singapore-business-district-and-2026-03-17-00-04-08-utc.webp',
    parties: ['Custodian Bank'],
  },
  {
    step: '04',
    badge: 'Mandate Alignment',
    title: 'Mandate Selection: Advisory or Discretionary',
    text: 'The client decides between an advisory mandate (client confirms each transaction) or discretionary mandate (b) (ASTRID manages within pre-agreed parameters and risk tolerance).',
    image: '/media/financial-analysis-and-planning-in-business-office-2026-09-21-18-23-55-utc.webp',
    parties: ['Client', 'ASTRID FIRST'],
  },
  {
    step: '05',
    badge: 'Institutional Link',
    title: 'Cooperation Agreement & Research Flow',
    text: 'Cooperation agreement established — providing operational support, receiving of institutional investment ideas and research, and unified client account information feeds.',
    image: '/media/double-exposure-digital-graphs-with-stacked-coins.webp',
    parties: ['ASTRID FIRST', 'Custodian Bank'],
  },
  {
    step: '06',
    badge: 'Client Outcome',
    title: 'Disciplined Execution & Peace of Mind',
    text: 'Clients are not burdened with stressful day-to-day investment decisions and enjoy institutional pricing, lower custody fees, and dedicated fiduciary service through ASTRID FIRST.',
    image: '/media/financial-growth-investment-success-with-stacks-gold-coins.webp',
    parties: ['Client', 'ASTRID FIRST', 'Custodian Bank'],
  },
];

export const BusinessModel = () => {
  useMotion();

  useEffect(() => {
    document.title = 'Our Business Model | ASTRID FIRST Wealth Advisory';
  }, []);

  return (
    <div className="business-model-page">
      <header className="page-head">
        <div className="page-head-inner">
          <p className="crumb">
            <a href="/">Home</a> / Our Business Model
          </p>
          <h1 data-reveal="chars">Our Business Model</h1>
          <p className="lead" data-reveal="fade">
            We never hold your assets. They stay with the custodian of your
            choice, and our authority stops at investment decisions.
          </p>
        </div>
      </header>

      <div className="spine">
        {/* Section 1: Understanding Our Business Model */}
        <section className="section shell">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">The structure</p>
              <h2 data-reveal="lines">
                Understanding Our Business Model for Sustainable Success
              </h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                With ASTRID FIRST's business model, the client and advisor work
                together to select an international private bank where assets
                are custodied.
              </p>
              <p data-reveal="fade">
                The client appoints ASTRID FIRST to manage the assets with a
                Limited Power of Attorney (LPOA) over the account, and pays an
                advisory fee for the ongoing service. ASTRID FIRST does not hold
                the client's assets; they are held with the custodian of their
                choice.
              </p>
              <p data-reveal="fade">
                The LPOA gives ASTRID FIRST the ability to invest and manage the
                assets, but not to transfer them out of the account.
                Instructions to move money out can only be given by the client
                directly to the custodian bank, which is what keeps the assets
                secure.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Full-Width Section: 3D Tripartite Model with Photo Background & Black Overlay + Roadmap Part */}
      <section className="roadmap-fullwidth-section" id="business-roadmap">
        {/* 1. Full-Width 3D Model Stage with Photo Background & 50% Black Overlay */}
        <div className="roadmap-3d-stage stage" aria-labelledby="roadmap-title">
          <div className="roadmap-3d-bg-wrap" aria-hidden="true">
            <img
              src="/media/high-angle-view-illuminated-city-buildings-night.webp"
              alt="Illuminated Financial Center at Night"
              className="roadmap-3d-bg-img"
            />
            <div className="roadmap-3d-overlay" />
            <div className="roadmap-3d-bottom-gradient" />
          </div>

          <div className="shell roadmap-3d-shell">
            <div className="roadmap-3d-intro">
              <p className="eyebrow roadmap-eyebrow">Step by step</p>
              <h2 id="roadmap-title" className="roadmap-title" data-reveal="lines">
                How the Relationship Between Client, ASTRID FIRST and the Bank Works
              </h2>
              <p className="lead roadmap-lead" data-reveal="fade">
                An interactive 3D tripartite model showing how our
                independent custody architecture keeps your assets secure while
                delivering institutional wealth management.
              </p>
            </div>

            <div className="roadmap-3d-full-scene" data-reveal="fade">
              <Suspense fallback={null}>
                <Roadmap3D />
              </Suspense>
            </div>
          </div>
        </div>

        {/* 2. Continue Directly to the Roadmap Part */}
        <div className="roadmap-timeline-stage">
          <div className="shell">
            <div className="roadmap-timeline-head">
              <p className="eyebrow">Working process</p>
              <h3 className="roadmap-timeline-title">
                Six-Step Implementation Roadmap
              </h3>
              <p className="roadmap-timeline-lead">
                From opening segregated accounts to executing bespoke investment
                strategies with complete transparency.
              </p>
            </div>

            <div className="roadmap-timeline">
              {roadmapSteps.map((step, index) => (
                <article
                  key={step.step}
                  className="roadmap-step-card"
                  data-reveal="group"
                >
                  <div className="roadmap-step-spine" aria-hidden="true">
                    <div className="roadmap-step-badge">{step.step}</div>
                    {index < roadmapSteps.length - 1 && (
                      <div className="roadmap-step-line" />
                    )}
                  </div>

                  <div className="roadmap-step-content">
                    <div className="roadmap-step-media">
                      <img
                        src={step.image}
                        alt={step.title}
                        loading="lazy"
                        className="roadmap-step-img"
                      />
                      <div className="roadmap-step-media-overlay" />
                    </div>

                    <div className="roadmap-step-body">
                      <h4 className="roadmap-step-title">{step.title}</h4>
                      <p className="roadmap-step-desc">{step.text}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="footnotes roadmap-footnotes">
              <p>
                <sup>(a)</sup> LPOA: Limited Power of Attorney. A limited
                authorisation strictly for investment decisions; the asset
                manager has no authority to transfer assets out of the
                account.
              </p>
              <p>
                <sup>(b)</sup> Discretionary vs advisory mandate: under a
                discretionary mandate the manager executes within agreed
                parameters; under advisory the client confirms each
                transaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="spine">
        {/* Section 3: Why engage an Independent Asset Manager */}
        <section className="section shell">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">The advantage</p>
              <h2 data-reveal="lines">
                Why engage an Independent Asset Manager?
              </h2>
            </div>
            <div className="rows" data-reveal="group">
              <div className="row">
                <h3>Independence</h3>
                <p>
                  We are not tied to any bank and are free of in-house product
                  distribution pressures. Our recommendations are driven solely
                  by your financial interests.
                </p>
              </div>
              <div className="row">
                <h3>Experienced Professionals</h3>
                <p>
                  Decades of combined experience across global institutions mean
                  you work with seasoned advisors who understand complex wealth
                  and market cycles.
                </p>
              </div>
              <div className="row">
                <h3>Continuity</h3>
                <p>
                  Bank relationship managers move on average every two to three
                  years. An independent asset manager provides a stable,
                  long-term advisory relationship across generations.
                </p>
              </div>
              <div className="row">
                <h3>One-Stop Multi-Platform Solution</h3>
                <p>
                  We coordinate across multiple custodian banks, consolidating
                  reporting and execution so you retain a coherent, unified
                  strategy.
                </p>
              </div>
              <div className="row">
                <h3>Competitive &amp; Transparent Pricing</h3>
                <p>
                  We negotiate institutional custody and transaction rates on
                  your behalf, eliminating hidden costs and passing institutional
                  pricing directly to you.
                </p>
              </div>
              <div className="row">
                <h3>Secure Asset Custody</h3>
                <p>
                  Your wealth remains safely custodied in your name with
                  top-tier global institutions in Singapore, Switzerland or
                  Hong Kong.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};